import type { Job, Company } from '../data/mockData';
import { jobs as mockJobs, companies as mockCompanies, getJobsByCompany } from '../data/mockData';

// Re-export Job/Company types so UI code depends on the API surface
// rather than importing from the mock data module directly.
export type { Job, Company };

export type JobsFilters = {
  query?: string;
  location?: string;
  jobType?: string;
  returnerFriendly?: boolean;
  remoteOnly?: boolean;
  sort?: 'newest' | 'comp';
  tags?: string[];
  minPay?: number | null;
  maxPay?: number | null;
  site?: 'jobs' | 'www' | 'both';
  occupationId?: string;
  page?: number;
  pageSize?: number;
};

export type JobDetail = Job & {
  employer?: Partial<Company>;
  employerId?: string | number;
  slug?: string;
  summary?: string | null;
  sanitizedHtml?: string | null;
  postedDate?: string;
  applyUrl?: string | null;
  occupationId?: string | null;
  occupationTitle?: string | null;
  occupation?: {
    id: string;
    preferredLabel: string;
    altLabels: string[];
    description?: string;
    source?: string;
  };
};

const API_BASE_URL = import.meta.env.VITE_API_URL;
const sanitizeBaseUrl = () => API_BASE_URL?.replace(/\/$/, '');
const PUBLIC_JOBS_PATH = '/v1/public/jobs';

const buildQueryString = (filters: JobsFilters) => {
  const params = new URLSearchParams();

  if (filters.query) params.set('query', filters.query);
  if (filters.location) params.set('location', filters.location);
  if (filters.jobType && filters.jobType !== 'all') params.set('jobType', filters.jobType);
  if (filters.occupationId) params.set('occupationId', filters.occupationId);
  if (filters.returnerFriendly) params.set('returnerFriendly', 'true');
  if (filters.remoteOnly) params.set('remoteOnly', 'true');
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.tags?.length) params.set('tags', filters.tags.join(','));
  if (typeof filters.minPay === 'number') params.set('minPay', String(filters.minPay));
  if (typeof filters.maxPay === 'number') params.set('maxPay', String(filters.maxPay));
  if (typeof filters.page === 'number') params.set('page', String(filters.page));
  if (typeof filters.pageSize === 'number') params.set('pageSize', String(filters.pageSize));
  params.set('site', filters.site ?? 'jobs');

  return params.toString();
};

const parseSalaryRange = (salary?: string) => {
  if (!salary) return null;
  const matches = salary.match(/\d+(?:[.,]\d+)?/g);
  if (!matches) return null;
  const numbers = matches
    .map((value) => Number.parseInt(value.replace(/[^\d]/g, ''), 10))
    .filter((value) => !Number.isNaN(value));
  if (!numbers.length) return null;
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  return { min, max };
};

const normaliseTags = (tags?: string[]) => tags?.map((tag) => tag.trim().toLowerCase()).filter(Boolean) ?? [];

const deriveJobTags = (job: Job) => {
  const explicitTags = normaliseTags((job as { tags?: string[] }).tags);
  const derived: string[] = [];
  if (job.category) derived.push(job.category);
  if (job.remote) derived.push('remote');
  if (job.returnerFriendly) derived.push('returner friendly');
  const requirementTags = job.requirements?.slice(0, 3) ?? [];
  derived.push(...requirementTags);
  return Array.from(new Set([...explicitTags, ...normaliseTags(derived)]));
};

const jobMatchesFilters = (job: Job, filters: JobsFilters) => {
  const normalisedQuery = filters.query?.trim().toLowerCase();
  const normalisedLocation = filters.location?.trim().toLowerCase();
  const jobTags = deriveJobTags(job);
  const requiredTags = normaliseTags(filters.tags);
  const salaryRange = parseSalaryRange(job.salary);

  if (
    normalisedQuery &&
    !(
      job.title.toLowerCase().includes(normalisedQuery) ||
      job.description.toLowerCase().includes(normalisedQuery) ||
      job.category.toLowerCase().includes(normalisedQuery)
    )
  ) {
    return false;
  }

  if (normalisedLocation && !job.location.toLowerCase().includes(normalisedLocation)) {
    return false;
  }

  if (filters.jobType && filters.jobType !== 'all' && job.type !== filters.jobType) {
    return false;
  }

  if (filters.returnerFriendly && !job.returnerFriendly) {
    return false;
  }

  if (filters.remoteOnly && !job.remote) {
    return false;
  }

  if (requiredTags.length && !requiredTags.every((tag) => jobTags.includes(tag))) {
    return false;
  }

  if (typeof filters.minPay === 'number') {
    const min = salaryRange?.min ?? 0;
    if (min < filters.minPay) return false;
  }

  if (typeof filters.maxPay === 'number') {
    const max = salaryRange?.max ?? salaryRange?.min ?? Number.POSITIVE_INFINITY;
    if (max > filters.maxPay) return false;
  }

  return true;
};

const sortJobs = (jobs: Job[], sort: JobsFilters['sort']) => {
  const copy = [...jobs];
  if (sort === 'comp') {
    return copy.sort((a, b) => {
      const aSalary = parseSalaryRange(a.salary)?.max ?? 0;
      const bSalary = parseSalaryRange(b.salary)?.max ?? 0;
      return bSalary - aSalary;
    });
  }
  return copy.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
};

const filterMockJobs = (jobs: Job[], filters: JobsFilters): JobDetail[] => {
  const filtered = jobs.filter((job) => jobMatchesFilters(job, filters));
  return sortJobs(filtered, filters.sort ?? 'newest').map((job) => ({
    ...job,
    summary: job.description ?? null,
    sanitizedHtml: job.description ? `<p>${job.description}</p>` : null,
    postedDate: job.posted ?? undefined,
  }));
};

const normaliseJobsResponse = async (response: Response) => {
  const payload = await response.json();
  if (Array.isArray(payload)) {
    return payload as Job[];
  }
  if (Array.isArray(payload?.data)) {
    return payload.data as Job[];
  }
  if (Array.isArray(payload?.jobs)) {
    return payload.jobs as Job[];
  }
  return mockJobs;
};

export async function fetchJobs(filters: JobsFilters = {}): Promise<Job[]> {
  if (!API_BASE_URL) {
    return filterMockJobs(mockJobs, filters);
  }

  const query = buildQueryString(filters);
  const url = `${sanitizeBaseUrl()}${PUBLIC_JOBS_PATH}${query ? `?${query}` : ''}`;

  try {
    const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      throw new Error(`Jobs request failed with ${response.status}`);
    }
    return await normaliseJobsResponse(response);
  } catch (error) {
    console.warn('[jobs] Falling back to mock data', error);
    return filterMockJobs(mockJobs, filters);
  }
}

const findMockJob = (identifier: string | number): JobDetail | undefined => {
  const numericId = Number(identifier);
  if (!Number.isNaN(numericId)) {
    const match = mockJobs.find((job) => job.id === numericId);
    return match
      ? {
          ...match,
          summary: match.description ?? null,
          sanitizedHtml: match.description ? `<p>${match.description}</p>` : null,
          postedDate: match.posted ?? undefined,
        }
      : undefined;
  }
  const slug = String(identifier).toLowerCase();
  const match = mockJobs.find((job) => {
    const jobSlug = (job as { slug?: string }).slug ?? String(job.id);
    return jobSlug.toLowerCase() === slug;
  });
  return match
    ? {
        ...match,
        summary: match.description ?? null,
        sanitizedHtml: match.description ? `<p>${match.description}</p>` : null,
        postedDate: match.posted ?? undefined,
      }
    : undefined;
};

const buildEmployerFromMock = (job?: Job) => {
  if (!job?.companyId) return undefined;
  const employer = mockCompanies.find((company) => company.id === job.companyId);
  return employer ? { ...employer } : undefined;
};

const normaliseJobDetailPayload = (payload: unknown): JobDetail | undefined => {
  if (!payload) return undefined;
  if (payload && typeof payload === 'object' && 'job' in (payload as Record<string, unknown>)) {
    const container = payload as Record<string, unknown>;
    return normaliseJobDetailPayload(container.job);
  }
  const jobLike = payload as Partial<JobDetail>;
  if (!jobLike) return undefined;
  return {
    ...jobLike,
    employer:
      jobLike.employer ??
      ((jobLike as { company?: Company }).company
        ? ((jobLike as { company?: Company }).company as Company)
        : undefined),
  } as JobDetail;
};

export async function fetchJobById(
  identifier: string | number,
  site: 'jobs' | 'www' | 'both' = 'jobs',
): Promise<JobDetail | undefined> {
  if (!API_BASE_URL) {
    const mockJob = findMockJob(identifier);
    if (!mockJob) return undefined;
    return { ...mockJob, employer: buildEmployerFromMock(mockJob) };
  }

  const url = new URL(`${sanitizeBaseUrl()}${PUBLIC_JOBS_PATH}/${identifier}`);
  url.searchParams.set('site', site);
  try {
    const response = await fetch(url.toString(), { headers: { 'Content-Type': 'application/json' } });
    if (response.status === 404) {
      return undefined;
    }
    if (!response.ok) {
      throw new Error(`Job detail request failed with ${response.status}`);
    }
    const payload = await response.json();
    const job = normaliseJobDetailPayload(payload);
    return job ?? undefined;
  } catch (error) {
    console.warn('[jobs] Job detail fetch failed', error);
    throw error instanceof Error ? error : new Error('Unable to load job detail');
  }
}

export async function fetchSimilarJobs(
  identifier: string | number,
  limit = 3,
  site: 'jobs' | 'www' | 'both' = 'jobs',
): Promise<Job[]> {
  if (!API_BASE_URL) {
    const fallback = findMockJob(identifier);
    if (!fallback) return [];
    return getJobsByCompany(fallback.companyId).filter((job) => job.id !== fallback.id).slice(0, limit);
  }

  const url = new URL(`${sanitizeBaseUrl()}${PUBLIC_JOBS_PATH}/${identifier}/similar`);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('site', site);
  try {
    const response = await fetch(url.toString(), { headers: { 'Content-Type': 'application/json' } });
    if (response.status === 404) {
      return [];
    }
    if (!response.ok) {
      throw new Error(`Similar jobs request failed with ${response.status}`);
    }
    const payload = await response.json();
    const data = Array.isArray(payload?.jobs) ? payload.jobs : Array.isArray(payload) ? payload : payload?.data;
    if (Array.isArray(data)) {
      return data as Job[];
    }
    return [];
  } catch (error) {
    console.warn('[jobs] Similar jobs fetch failed', error);
    throw error instanceof Error ? error : new Error('Unable to load similar jobs');
  }

}
